import { ReadonlySignal, createSignal } from '@funtech-int/rival';

type ScrollDirection = 'start' | 'end';

interface ScrollControllerConfig {
    itemSelector?: string;
    onResize?: (width: number, height: number) => void;
}

interface ScrollController {
    startReached: ReadonlySignal<boolean>;
    endReached: ReadonlySignal<boolean>;
    scrollTo: (this: void, direction: ScrollDirection, scrollDistance: number) => void;
    scrollToIndex: (this: void, index: number, options?: ScrollIntoViewOptions) => void;
    scrollToElement: (this: void, element: HTMLElement, options?: ScrollIntoViewOptions) => void;
    setContainer: (this: void, container: HTMLElement) => void;
    refresh: (this: void) => void;
    destroy: () => void;
}

export const createScrollController = ({
    itemSelector,
    onResize,
}: ScrollControllerConfig): ScrollController => {
    const startReached = createSignal(true);
    const endReached = createSignal(true);

    let container: HTMLElement | null = null;
    let resizeObserver: ResizeObserver | null = null;
    let intersectionObserver: IntersectionObserver | null = null;
    let isDestroyed = false;

    let firstItem: HTMLElement | null = null;
    let lastItem: HTMLElement | null = null;

    const getItems = (): HTMLElement[] => {
        if (!container) return [];
        return itemSelector ? Array.from(container.querySelectorAll(itemSelector)) : [];
    };

    const updateObservedItems = () => {
        if (!intersectionObserver) return;

        const items = getItems();

        if (firstItem) intersectionObserver.unobserve(firstItem);
        if (lastItem && lastItem !== firstItem) intersectionObserver.unobserve(lastItem);

        if (items.length === 0) {
            firstItem = null;
            lastItem = null;
            startReached.set(true);
            endReached.set(true);
            return;
        }

        if (items.length === 1) {
            startReached.set(true);
            endReached.set(true);
        }

        firstItem = items[0] ?? null;
        lastItem = items[items.length - 1] ?? null;

        if (firstItem) {
            intersectionObserver.observe(firstItem);
        }

        if (lastItem !== firstItem && lastItem) {
            intersectionObserver.observe(lastItem);
        }
    };

    const handleResize = () => {
        if (isDestroyed || !container) return;
        onResize?.(container.offsetWidth, container.offsetHeight);
    };

    const refresh = () => {
        updateObservedItems();
    };

    const cleanup = () => {
        resizeObserver?.disconnect();
        intersectionObserver?.disconnect();

        intersectionObserver = null;
        firstItem = null;
        lastItem = null;
    };

    const setupIntersectionObserver = () => {
        if (!container) return;

        intersectionObserver = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    const isFirstItem = entry.target === firstItem;
                    const isLastItem = entry.target === lastItem;

                    const isVisible = entry.isIntersecting;

                    if (isFirstItem) {
                        startReached.set(isVisible);
                    }

                    if (isLastItem) {
                        endReached.set(isVisible);
                    }
                });
            },
            {
                root: container,
                rootMargin: '0px',
                threshold: [0.9, 1],
            }
        );
    };

    const init = () => {
        if (!container) return;

        setupIntersectionObserver();
        updateObservedItems();

        resizeObserver = new ResizeObserver(handleResize);
        resizeObserver.observe(container);
    };

    const setContainer = (newContainer: HTMLElement | null) => {
        if (isDestroyed) return;

        if (container === newContainer) return;

        cleanup();

        container = newContainer;

        if (!container) {
            startReached.set(true);
            endReached.set(true);
            return;
        }

        init();
    };

    const scrollTo = (direction: ScrollDirection, scrollDistance: number): void => {
        if (isDestroyed || !container) return;

        const isRTL = document.dir === 'rtl';
        const shouldScrollNegative =
            (direction === 'start' && !isRTL) || (direction === 'end' && isRTL);

        container.scrollBy({
            left: shouldScrollNegative ? -scrollDistance : scrollDistance,
            behavior: 'smooth',
        });
    };

    const scrollToElement = (element: HTMLElement, options?: ScrollIntoViewOptions): void => {
        if (isDestroyed || !container || !container.contains(element)) return;

        const { behavior = 'smooth', block = 'nearest', inline = 'start' } = options ?? {};

        element.scrollIntoView({
            behavior,
            block,
            inline,
        });
    };

    const scrollToIndex = (index: number, options?: ScrollIntoViewOptions): void => {
        if (isDestroyed || !container) return;

        const items = getItems();
        const target = items[index];

        if (!target) return;

        scrollToElement(target, options);
    };

    const destroy = (): void => {
        if (isDestroyed) return;

        isDestroyed = true;
        cleanup();

        resizeObserver = null;
        intersectionObserver = null;
        container = null;
    };

    return {
        startReached,
        endReached,
        scrollTo,
        scrollToIndex,
        scrollToElement,
        setContainer,
        refresh,
        destroy,
    };
};
